import React, { useEffect, useMemo, useRef, useState } from "react";

import "../assets/css/Management.css";
import Header from "../components/Header";
import banner from "../assets/img/schoolbanner/schoolofeng.webp";
import SectionTitle from "../school-components/SectionTitle";
import students1 from "../assets/img/engineering-students-working-in-a-laboratory.avif";
import students2 from "../assets/img/student-engineering-project.png";
import learningImage from "../assets/img/management-learning.webp";
import Slider from "react-slick";
import Faculty from "../school-components/Faculty"
import recruiters from "../school-components/Recruiters"
import Alumni from "../home-components/Alumni";
import Footer from "../components/Footer";
import ManagementLeadFooterSection from "../school-components/ManagementLeadFooterSection";

const programmes = [
    {
        level: "doctoral",
        degree: "Ph.D.",
        title: "Management",
        duration: "3 Years",
        eligibility: "Relevant postgraduate qualification",
        description:
            "Undertake advanced research in business, management, entrepreneurship, finance, marketing and organizational studies under experienced faculty guidance.",
    },
    {
        level: "postgraduate",
        degree: "MBA",
        title: "Master of Business Administration",
        duration: "2 Years (Full Time)",
        eligibility: "Bachelor's degree in any discipline",
        description:
            "Develop leadership, strategic thinking and managerial expertise through industry-oriented learning in finance, marketing, HR, operations and business analytics.",
    },
    {
        level: "postgraduate",
        degree: "EMBA",
        title: "Executive Master of Business Administration",
        duration: "18 Months",
        eligibility: "Bachelor's degree with relevant work experience",
        description:
            "Designed for working professionals to strengthen leadership capabilities, strategic decision-making and business management skills while continuing their careers.",
    },
    {
        level: "undergraduate",
        degree: "BBA",
        title: "Bachelor of Business Administration",
        duration: "3 Years",
        eligibility: "10+2 or Equivalent",
        description:
            "Build a strong foundation in business management, entrepreneurship, marketing, finance, human resources and organizational behaviour through practical learning.",
    },
    {
        level: "postgraduatediploma",
        degree: "PGDBA",
        title: "Post Graduate Diploma in Business Administration",
        duration: "1 Year",
        eligibility: "Bachelor's degree in any discipline",
        description:
            "Gain industry-focused managerial knowledge with practical exposure to business operations, strategic planning and organizational leadership.",
    },
    {
        level: "postgraduatediploma",
        degree: "PGDBM",
        title: "Post Graduate Diploma in Business Management",
        duration: "2 Years",
        eligibility: "Bachelor's degree in any discipline",
        description:
            "Enhance professional skills in management, finance, marketing, operations and business communication for successful corporate careers.",
    },
    {
        level: "postgraduatediploma",
        degree: "PGDM",
        title: "Postgraduate Diploma in Management",
        duration: "2 Years",
        eligibility: "Bachelor's degree in any discipline",
        description:
            "Prepare for leadership roles through an industry-driven curriculum covering business strategy, analytics, innovation and organizational management.",
    },
    {
        level: "diploma",
        degree: "DBA",
        title: "Diploma in Business Administration",
        duration: "1 Year",
        eligibility: "10+2 or Equivalent",
        description:
            "Acquire practical knowledge of business administration, office management, accounting fundamentals and customer relationship management for entry-level managerial roles.",
    },
];

const learningStages = [
    {
        number: "01",
        label: "Explore",
        title: "Build business perspective",
        text: "Foundation courses, live business conversations and market observation help students understand how organisations and economies work.",
        tags: ["Business foundations", "Industry talks", "Market immersion"],
    },
    {
        number: "02",
        label: "Practise",
        title: "Solve decisions, not worksheets",
        text: "Case discussions, simulations and analytics labs place students inside realistic management situations with incomplete information.",
        tags: ["Case method", "Business simulation", "Analytics lab"],
    },
    {
        number: "03",
        label: "Lead",
        title: "Learn through responsibility",
        text: "Team consulting assignments, student clubs and presentations develop ownership, communication, negotiation and collaborative leadership.",
        tags: ["Live projects", "Leadership labs", "Peer learning"],
    },
    {
        number: "04",
        label: "Launch",
        title: "Translate learning into a career",
        text: "Internships, mentoring and placement preparation connect academic capability with the expectations of employers and entrepreneurs.",
        tags: ["Internships", "Career mentoring", "Venture support"],
    },
];

const outcomes = [
    { value: 92, suffix: "%", label: "Experiential learning", note: "Projects, cases and field exposure" },
    { value: 30, suffix: "+", label: "Industry interactions", note: "Leadership sessions each academic year" },
    { value: 1, suffix: ":15", label: "Mentoring support", note: "Focused academic and career guidance" },
];

const capabilities = [
    ["01", "Strategic thinking", "Evaluate markets, organisations and long-term business choices."],
    ["02", "Data-led decisions", "Interpret evidence and communicate recommendations with clarity."],
    ["03", "Responsible leadership", "Lead diverse teams with ethics, empathy and accountability."],
    ["04", "Entrepreneurial action", "Identify opportunities and move ideas towards viable solutions."],
];

const priorities = [
    {
        number: "01",
        title: "Industry-relevant learning",
        text: "Connecting management principles with current business practice.",
    },
    {
        number: "02",
        title: "Responsible leadership",
        text: "Developing ethical, empathetic and accountable decision-makers.",
    },
    {
        number: "03",
        title: "Entrepreneurial thinking",
        text: "Encouraging students to question, innovate and create meaningful value.",
    },
];

const placementStats = [
    { value: 6500, suffix: "+", label: "Placement outcomes" },
    { value: 250, suffix: "+", label: "Hiring partners" },
    { value: 150, suffix: "+", label: "Campus recruiters" },
    { value: 95, suffix: "%", label: "Students placed" },
];

const careerJourney = [
    {
        number: "01",
        title: "Discover",
        text: "Aptitude mapping and one-to-one counselling help students identify suitable career directions.",
    },
    {
        number: "02",
        title: "Prepare",
        text: "Resume clinics, communication labs and aptitude sessions build essential employability skills.",
    },
    {
        number: "03",
        title: "Experience",
        text: "Internships, corporate projects and industry interactions provide meaningful workplace exposure.",
    },
    {
        number: "04",
        title: "Perform",
        text: "Mock interviews, group discussions and role-specific preparation strengthen selection readiness.",
    },
    {
        number: "05",
        title: "Progress",
        text: "Placement coordination and alumni mentoring support students as they begin their professional journey.",
    },
];

const admissionSteps = [
    {
        number: "01",
        title: "Choose your programme",
        text: "Explore programme structure, duration, specialisations and career pathways.",
    },
    {
        number: "02",
        title: "Submit application",
        text: "Complete the online form with accurate personal and academic information.",
    },
    {
        number: "03",
        title: "Upload documents",
        text: "Provide the required academic records, identification and recent photograph.",
    },
    {
        number: "04",
        title: "Eligibility review",
        text: "The admissions team verifies the application against current programme norms.",
    },
    {
        number: "05",
        title: "Confirm admission",
        text: "Eligible applicants receive further instructions to complete enrolment.",
    },
];

const programmes2 = [
    {
        id: "phd-management",
        shortName: "Ph.D.",
        name: "Doctor of Philosophy in Management",
        level: "Doctoral",
        duration: "3 Years",
        qualification: "Relevant postgraduate qualification",
        eligibility:
            "Candidates should hold an eligible postgraduate qualification and meet the requirements prescribed under current University and regulatory norms.",
        selection:
            "Admission is subject to the applicable entrance, interview, research proposal and regulatory requirements.",
        idealFor:
            "Undertake advanced research in business, management, entrepreneurship, finance, marketing and organizational studies under experienced faculty guidance.",
    },
    {
        id: "mba",
        shortName: "MBA",
        name: "Master of Business Administration",
        level: "Postgraduate",
        duration: "2 Years",
        qualification: "Bachelor's degree in any discipline",
        eligibility:
            "Candidates should hold a Bachelor’s degree in any recognised discipline and satisfy the minimum marks prescribed by the University.",
        selection:
            "Selection may consider academic records and any additional interaction or assessment required under current admission guidelines.",
        idealFor:
            "Develop leadership, strategic thinking and managerial expertise through industry-oriented learning in finance, marketing, HR, operations and business analytics.",
    },
    {
        id: "emba",
        shortName: "EMBA",
        name: "Executive Master of Business Administration",
        level: "Postgraduate",
        duration: "18 Months",
        qualification: "Bachelor's degree with relevant work experience",
        eligibility:
            "Candidates should hold a recognised Bachelor’s degree together with relevant professional experience.",
        selection:
            "The application may be reviewed against academic background, professional experience and current programme requirements.",
        idealFor:
            "Designed for working professionals to strengthen leadership capabilities, strategic decision-making and business management skills while continuing their careers.",
    },
    {
        id: "bba",
        shortName: "BBA",
        name: "Bachelor of Business Administration",
        level: "Undergraduate",
        duration: "3 Years",
        qualification: "10+2 or Equivalent",
        eligibility:
            "Candidates should have successfully completed 10+2 or an equivalent examination from a recognised board.",
        selection:
            "Admission is considered through academic eligibility and the University’s prevailing admission process.",
        idealFor:
            "Build a strong foundation in business management, entrepreneurship, marketing, finance, human resources and organizational behaviour through practical learning.",
    },
    {
        id: "pgdba",
        shortName: "PGDBA",
        name: "Post Graduate Diploma in Business Administration",
        level: "Postgraduate Diploma",
        duration: "1 Year",
        qualification: "Bachelor's degree in any discipline",
        eligibility:
            "Candidates should hold a Bachelor’s degree in any discipline from a recognised institution.",
        selection:
            "Admission is considered through academic eligibility and the University’s prevailing admission process.",
        idealFor:
            "Gain industry-focused managerial knowledge with practical exposure to business operations, strategic planning and organizational leadership.",
    },
    {
        id: "pgdbm",
        shortName: "PGDBM",
        name: "Post Graduate Diploma in Business Management",
        level: "Postgraduate Diploma",
        duration: "2 Years",
        qualification: "Bachelor's degree in any discipline",
        eligibility:
            "Candidates should hold a Bachelor’s degree in any discipline from a recognised institution.",
        selection:
            "Admission is considered through academic eligibility and the University’s prevailing admission process.",
        idealFor:
            "Enhance professional skills in management, finance, marketing, operations and business communication for successful corporate careers.",
    },
    {
        id: "pgdm",
        shortName: "PGDM",
        name: "Postgraduate Diploma in Management",
        level: "Postgraduate Diploma",
        duration: "2 Years",
        qualification: "Bachelor's degree in any discipline",
        eligibility:
            "Candidates should hold a Bachelor’s degree in any discipline from a recognised institution.",
        selection:
            "Selection may consider academic records and any additional interaction or assessment required under current admission guidelines.",
        idealFor:
            "Prepare for leadership roles through an industry-driven curriculum covering business strategy, analytics, innovation and organizational management.",
    },
    {
        id: "dba",
        shortName: "DBA",
        name: "Diploma in Business Administration",
        level: "Diploma",
        duration: "1 Year",
        qualification: "10+2 or Equivalent",
        eligibility:
            "Candidates should have successfully completed 10+2 or an equivalent examination from a recognised board.",
        selection:
            "Admission is considered through academic eligibility and the University’s prevailing admission process.",
        idealFor:
            "Acquire practical knowledge of business administration, office management, accounting fundamentals and customer relationship management for entry-level managerial roles.",
    },
];

const documents = [
    "Academic mark sheets",
    "Qualifying certificate",
    "Government identity proof",
    "Recent passport photograph",
    "Transfer or migration document",
    "Category certificate, if applicable",
];

const faqs = [
    {
        category: "Programmes",
        question: "Which programmes are offered by the School of Management?",
        answer:
            "The School offers Ph.D. in Management, MBA, Executive MBA, BBA, PGDBA, PGDBM, PGDM and Diploma in Business Administration. Programme availability may vary by academic session, so applicants should confirm the current intake before applying.",
    },
    {
        category: "Programmes",
        question: "How do I choose between MBA, PGDM, PGDBM and PGDBA?",
        answer:
            "The right choice depends on your academic background, career objective, preferred duration and desired depth of study. MBA and two-year postgraduate diplomas offer broader management development, while a one-year PGDBA provides a more compact route. The admissions team can help you compare the current curriculum and outcomes.",
    },
    {
        category: "Eligibility",
        question: "What is the eligibility for the BBA programme?",
        answer:
            "Applicants should have completed 10+2 or an equivalent examination from a recognised board. Final admission remains subject to document verification and the University’s current eligibility regulations.",
    },
    {
        category: "Eligibility",
        question: "Can graduates from any discipline apply for MBA programmes?",
        answer:
            "Yes. Candidates with a recognised Bachelor’s degree in any discipline may apply, provided they satisfy the minimum academic and admission requirements prescribed for the current session.",
    },
    {
        category: "Eligibility",
        question: "Is professional experience required for the Executive MBA?",
        answer:
            "The Executive MBA is intended for working professionals, and relevant professional experience may be required. Applicants should confirm the current experience criteria and supporting documents with the admissions office.",
    },
    {
        category: "Learning",
        question: "How are management programmes taught?",
        answer:
            "Learning may combine classroom discussion, business cases, presentations, simulations, group assignments, field exposure, live projects and interaction with industry professionals. The objective is to connect management concepts with practical decision-making.",
    },
    {
        category: "Learning",
        question: "Can students choose management specialisations?",
        answer:
            "Specialisation options may include areas such as Marketing, Finance, Human Resource Management, Operations and Business Analytics, depending on the programme and current academic structure. Students should review the latest curriculum for confirmed options.",
    },
    {
        category: "Learning",
        question: "Are internships and live projects part of the learning experience?",
        answer:
            "Internships, industry assignments and project-based learning may form an important part of management education. Their format, duration and assessment requirements depend on the selected programme and academic regulations.",
    },
    {
        category: "Careers",
        question: "What career support is available to management students?",
        answer:
            "Career Services supports students through aptitude development, resume preparation, communication practice, mock interviews, group discussions, internship guidance and placement coordination. Participation and eligibility remain subject to applicable placement policies.",
    },
    {
        category: "Careers",
        question: "Which career areas can management graduates explore?",
        answer:
            "Graduates may explore opportunities across marketing, sales, finance, banking, human resources, operations, business development, analytics, consulting, entrepreneurship and other management functions, depending on their programme, skills and experience.",
    },
    {
        category: "Admissions",
        question: "Which documents are generally required during admission?",
        answer:
            "Applicants generally need academic mark sheets, qualifying certificates, identity proof, recent photographs and applicable migration, transfer or category documents. The exact checklist is communicated during the application process.",
    },
    {
        category: "Admissions",
        question: "How can I get help before submitting my application?",
        answer:
            "You can contact the University admissions team for guidance on programme selection, eligibility, documents, fees and application steps. Always rely on the latest official admission communication before making a payment or final decision.",
    },
];

const categories = ["All", "Programmes", "Eligibility", "Learning", "Careers", "Admissions"];

function Counter({ value, suffix = "" }) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                const duration = 1400;
                const start = performance.now();

                const animate = (now) => {
                    const progress = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - progress, 3);
                    setCount(Math.round(value * eased));
                    if (progress < 1) requestAnimationFrame(animate);
                };

                requestAnimationFrame(animate);
                observer.disconnect();
            },
            { threshold: 0.45 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [value]);

    return (
        <strong ref={ref}>
            {count}
            {suffix}
        </strong>
    );
}

function AnimatedValue({ value, suffix, active }) {
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!active) return;
        let frame;
        const duration = 1200;
        const start = performance.now();
        const animate = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(value * eased));
            if (progress < 1) frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [active, value]);

    return <>{display}{suffix}</>;
}

function Management() {
    const [programmeFilter, setProgrammeFilter] = useState("all");
    const [openFaq, setOpenFaq] = useState(0);

    const visibleProgrammes = useMemo(
        () =>
            programmeFilter === "all"
                ? programmes
                : programmes.filter((item) => item.level === programmeFilter),
        [programmeFilter]
    );

    useEffect(() => {
        const elements = document.querySelectorAll(
            ".school-page .reveal:not(.is-visible)"
        );

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    console.log(entry);

                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.08,
                rootMargin: "0px 0px -30px 0px",
            }
        );

        elements.forEach((element) => {
            observer.observe(element);
        });

        return () => {
            observer.disconnect();
        };
    }, [programmeFilter]);

    const [activeStage, setActiveStage] = useState(0);
    const [outcomesVisible, setOutcomesVisible] = useState(false);
    const outcomesRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setOutcomesVisible(true),
            { threshold: 0.25 }
        );
        if (outcomesRef.current) observer.observe(outcomesRef.current);
        return () => observer.disconnect();
    }, []);


    const statsRef = useRef(null);
    const [animateStats, setAnimateStats] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setAnimateStats(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.25 }
        );

        if (statsRef.current) observer.observe(statsRef.current);
        return () => observer.disconnect();
    }, []);

    const [activeProgramme, setActiveProgramme] = useState(programmes[0]);

    const [activeCategory, setActiveCategory] = useState("Programmes");
    const [search, setSearch] = useState("");
    const [openQuestion, setOpenQuestion] = useState(0);

    const visibleFaqs = useMemo(() => {
        const term = search.trim().toLowerCase();

        return faqs.filter((item) => {
            const matchesCategory =
                activeCategory === "All" || item.category === activeCategory;
            const matchesSearch =
                !term ||
                item.question.toLowerCase().includes(term) ||
                item.answer.toLowerCase().includes(term);

            return matchesCategory && matchesSearch;
        });
    }, [activeCategory, search]);

    const chooseCategory = (category) => {
        setActiveCategory(category);
        setOpenQuestion(0);
    };

    return (
        <>
            <Header />
            <main className="school-page">
                <section className="school-hero" id="overview">
                    <img
                        className="school-hero__image"
                        src={banner}
                        alt="University engineering campus"
                    />
                    <div className="school-hero__shade" />
                    <div className="school-container school-hero__content">
                        <div className="school-breadcrumb">
                            <a href="/">Home</a><span>/</span><a href="/academics">Academics</a>
                            <span>/</span><b>Management</b>
                        </div>
                        <span className="school-hero__label">School of</span>
                        <h1>Management</h1>
                        <p>
                            Welcome to the School of Management at ISBM University! Our goal is to provide students with a comprehensive education in business and management, equipping them with the knowledge and skills needed to succeed in today's global business environment.
                        </p>
                        <div className="school-hero__actions">
                            <a className="school-button school-button--primary" href="/apply-online">
                                Apply for Admission <span>↗</span>
                            </a>
                            <a className="school-button school-button--glass" href="#programmes">
                                Explore Programmes <span>↓</span>
                            </a>
                        </div>
                    </div>

                    <div className="school-hero__stats">
                        <div><Counter value={8} suffix="+" /><span>Industry-Aligned Management Programmes</span></div>
                        <div><Counter value={50} suffix="+" /><span>Top Corporate Recruiting Partners</span></div>
                        <div><Counter value={9} suffix="+" /><span>National & International Recognitions</span></div>
                        <div><Counter value={100} suffix="%" /><span>Placement Support</span></div>
                    </div>
                </section>

                {/* Programmes */}
                <section className="school-programmes school-section" id="programmes">
                    <div className="school-container">
                        <div className="school-programmes__header reveal">
                            <SectionTitle
                                eyebrow="Academic programmes"
                                title="Choose a pathway."
                                accent="Create your future."
                                description="Explore programmes designed around strong foundations, emerging technologies and the skills demanded by modern industry."
                            />
                            <div className="school-filter" aria-label="Filter programmes">
                                {[
                                    ["all", "All"],
                                    ["diploma", "Diploma"],
                                    ["undergraduate", "Undergraduate"],
                                    ["postgraduatediploma", "Postgraduate Diploma"],
                                    ["postgraduate", "Postgraduate"],
                                    ["doctoral", "Doctoral"],
                                ].map(([value, label]) => (
                                    <button
                                        type="button"
                                        key={value}
                                        className={programmeFilter === value ? "active" : ""}
                                        onClick={() => setProgrammeFilter(value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="school-programme-list">
                            {visibleProgrammes.map((item, index) => (
                                <article className="school-programme reveal" key={`${item.degree}-${item.title}`}>
                                    <span className="school-programme__number">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <div className="school-programme__degree">
                                        <small>{item.level}</small><b>{item.degree}</b>
                                    </div>
                                    <div className="school-programme__copy">
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                        <div><span>Duration: {item.duration}</span><span>Eligibility: {item.eligibility}</span></div>
                                    </div>
                                    <a href="/programme-details" aria-label={`View ${item.title}`}>↗</a>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* About */}
                <section className="school-about school-section">
                    <div className="school-container school-about__grid">
                        <div className="school-about__visual reveal">
                            <div className="school-about__main-image">
                                <img src={students1} alt="Engineering students working in a laboratory"
                                />
                            </div>
                            <div className="school-about__small-image">
                                <img src={students2} alt="Student engineering project" />
                            </div>
                            <div className="school-about__seal">
                                <b>Learn</b><span>Design · Build · Lead</span>
                            </div>
                        </div>

                        <div className="school-about__copy reveal">
                            <SectionTitle
                                eyebrow="About the school"
                                title="Where knowledge meets"
                                accent="possibility."
                            />
                            <p className="school-about__lead">
                                We nurture technically strong, socially responsible and
                                future-ready professionals through rigorous academics,
                                interdisciplinary exploration and hands-on problem solving.
                            </p>
                            <p>
                                Our curriculum connects engineering fundamentals with emerging
                                technologies, industry exposure, research opportunities and
                                entrepreneurial thinking. Students learn in an environment
                                designed to encourage experimentation and collaboration.
                            </p>
                            <div className="school-about__points">
                                <span>✓ Outcome-based curriculum</span>
                                <span>✓ Industry-aligned learning</span>
                                <span>✓ Innovation and incubation</span>
                                <span>✓ Career readiness training</span>
                            </div>
                            <a className="school-text-link" href="/about-school">
                                Discover our academic vision <span>↗</span>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Dean Message */}
                <section className="dms" aria-labelledby="dean-message-heading">
                    <div className="dms__container">
                        <div className="dms__shell">
                            <header className="dms__header">
                                <div>
                                    <span className="dms__eyebrow">From the Dean’s desk</span>
                                    <h2 id="dean-message-heading">
                                        A message on learning,
                                        <em> leadership and purpose.</em>
                                    </h2>
                                </div>

                                <span className="dms__edition" aria-label="School leadership message">
                                    <small>School of</small>
                                    <strong>Management</strong>
                                </span>
                            </header>

                            <div className="dms__body">
                                <article className="dms__message">
                                    <span className="dms__quote" aria-hidden="true">“</span>

                                    <p className="dms__opening">
                                        Management education must prepare students not only to enter
                                        organisations, but to improve them.
                                    </p>

                                    <div className="dms__copy">
                                        <p>
                                            At the School of Management, we create an academic environment
                                            where knowledge is tested through discussion, application and
                                            reflection. Our students engage with business cases, live
                                            projects, industry leaders and collaborative assignments that
                                            strengthen both professional ability and personal confidence.
                                        </p>
                                        <p>
                                            We encourage every learner to remain curious, act responsibly
                                            and approach complex challenges with an open mind. Our purpose
                                            is to nurture thoughtful professionals who can make informed
                                            decisions, lead diverse teams and contribute meaningfully to
                                            business and society.
                                        </p>
                                    </div>

                                    <footer className="dms__author">
                                        <div>
                                            <strong>Dean, School of Management</strong>
                                        </div>
                                    </footer>
                                </article>

                                <aside className="dms__priorities" aria-label="Dean's academic priorities">
                                    <div className="dms__priorities-head">
                                        <span>Our academic direction</span>
                                        <small>Three priorities</small>
                                    </div>

                                    <div className="dms__priority-list">
                                        {priorities.map((priority) => (
                                            <div className="dms__priority" key={priority.number}>
                                                <span>{priority.number}</span>
                                                <div>
                                                    <h3>{priority.title}</h3>
                                                    <p>{priority.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <blockquote>
                                        “Learn with curiosity. Lead with character.”
                                    </blockquote>
                                </aside>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Learning Section  */}
                <section className="mls" aria-labelledby="mls-title">
                    <div className="mls__intro">
                        <div className="mls__container mls__intro-grid">
                            <div className="mls__heading">
                                <span className="mls__eyebrow">The management learning experience</span>
                                <h2 id="mls-title">
                                    Where business knowledge becomes
                                    <em> confident action.</em>
                                </h2>
                            </div>
                            <p className="mls__lead">
                                Students learn inside an active management environment—questioning
                                assumptions, testing ideas, working with data and presenting
                                decisions. Every semester moves them closer to professional
                                confidence.
                            </p>
                        </div>
                    </div>

                    <div className="mls__journey">
                        <div className="mls__container">
                            <div className="mls__journey-shell">
                                <div className="mls__visual">
                                    <img
                                        src={learningImage}
                                        alt="Indian management students collaborating during an applied learning session"
                                        loading="lazy"
                                    />
                                    <div className="mls__visual-caption">
                                        <span>Learning environment</span>
                                        <strong>Collaborative. Applied. Industry-aware.</strong>
                                    </div>
                                    <div className="mls__image-index" aria-hidden="true">
                                        {learningStages[activeStage].number}
                                    </div>
                                </div>

                                <div className="mls__method">
                                    <div className="mls__method-top">
                                        <span>How students learn</span>
                                        <small>{activeStage + 1} / {learningStages.length}</small>
                                    </div>

                                    <div className="mls__stage-nav" role="tablist" aria-label="Learning stages">
                                        {learningStages.map((stage, index) => (
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={activeStage === index}
                                                className={activeStage === index ? "is-active" : ""}
                                                onClick={() => setActiveStage(index)}
                                                key={stage.label}
                                            >
                                                <i>{stage.number}</i>
                                                <span>{stage.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="mls__stage-content" role="tabpanel" aria-live="polite">
                                        <span className="mls__stage-kicker">
                                            Stage {learningStages[activeStage].number}
                                        </span>
                                        <h3>{learningStages[activeStage].title}</h3>
                                        <p>{learningStages[activeStage].text}</p>
                                        <div className="mls__tags">
                                            {learningStages[activeStage].tags.map((tag) => (
                                                <span key={tag}>{tag}</span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mls__environment">
                                        <span>Supported by</span>
                                        <ul>
                                            <li>Smart classrooms</li>
                                            <li>Management library</li>
                                            <li>Incubation guidance</li>
                                            <li>Corporate mentorship</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mls__outcomes" ref={outcomesRef}>
                        <div className="mls__container">
                            <div className="mls__outcomes-head">
                                <div>
                                    <span className="mls__eyebrow mls__eyebrow--light">School outcomes</span>
                                    <h2>Capabilities that travel beyond the classroom.</h2>
                                </div>
                                <p>
                                    Graduates leave with more than subject knowledge. They develop
                                    the judgement, communication and adaptability required to create
                                    value in changing organisations.
                                </p>
                            </div>

                            <div className="mls__evidence">
                                {outcomes.map((outcome) => (
                                    <div className="mls__metric" key={outcome.label}>
                                        <strong>
                                            <AnimatedValue
                                                value={outcome.value}
                                                suffix={outcome.suffix}
                                                active={outcomesVisible}
                                            />
                                        </strong>
                                        <div>
                                            <h3>{outcome.label}</h3>
                                            <p>{outcome.note}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mls__capabilities">
                                {capabilities.map(([number, title, text]) => (
                                    <article key={title}>
                                        <span>{number}</span>
                                        <div>
                                            <h3>{title}</h3>
                                            <p>{text}</p>
                                        </div>
                                        <i aria-hidden="true">↗</i>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Faculty & Research Mentors */}
                <Faculty />

                {/* Career Services & Placements */}
                <section className="cps" aria-labelledby="career-placement-title">
                    <div className="cps__hero">
                        <div className="cps__container cps__hero-grid">
                            <div className="cps__heading">
                                <span className="cps__eyebrow">Career services & placements</span>
                                <h2 id="career-placement-title">
                                    From classroom capability to
                                    <em> career confidence.</em>
                                </h2>
                                <p>
                                    Our Career Services team works with students throughout their
                                    academic journey—helping them understand their strengths, build
                                    professional skills, gain industry exposure and approach
                                    opportunities with confidence.
                                </p>

                                <div className="cps__actions">
                                    <a href="/placements" className="cps__button cps__button--primary">
                                        Explore placements <span>↗</span>
                                    </a>
                                    <a href="/corporate-relations" className="cps__button cps__button--text">
                                        Partner with us <span>→</span>
                                    </a>
                                </div>
                            </div>

                            <div className="cps__salary-panel">
                                <span className="cps__salary-label">Placement highlight</span>
                                <div className="cps__salary">
                                    <strong>45</strong>
                                    <span>
                                        <b>LPA</b>
                                        Highest offer
                                    </span>
                                </div>
                                <div className="cps__salary-line">
                                    <span>
                                        <small>25 LPA</small>
                                        Average offer
                                    </span>
                                    <span>
                                        <small>Multi-sector</small>
                                        Opportunities
                                    </span>
                                </div>
                                <p>
                                    Career opportunities across management, technology, banking,
                                    consulting, operations and emerging business functions.
                                </p>
                            </div>
                        </div>

                        <div className="cps__container">
                            <div className="cps__stats" ref={statsRef}>
                                {placementStats.map((stat) => (
                                    <div className="cps__stat" key={stat.label}>
                                        <strong>
                                            <Counter
                                                value={stat.value}
                                                suffix={stat.suffix}
                                                run={animateStats}
                                            />
                                        </strong>
                                        <span>{stat.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="cps__journey-section">
                        <div className="cps__container">
                            <div className="cps__section-head">
                                <div>
                                    <span className="cps__eyebrow">The placement pathway</span>
                                    <h2>Career preparation begins before the interview.</h2>
                                </div>
                                <p>
                                    A structured progression develops self-awareness, workplace
                                    exposure and selection readiness across every stage of the
                                    student experience.
                                </p>
                            </div>

                            <div className="cps__journey">
                                <div className="cps__journey-line" aria-hidden="true" />
                                {careerJourney.map((step) => (
                                    <article className="cps__journey-step" key={step.number}>
                                        <span className="cps__step-number">{step.number}</span>
                                        <div className="cps__step-dot" aria-hidden="true" />
                                        <h3>{step.title}</h3>
                                        <p>{step.text}</p>
                                    </article>
                                ))}
                            </div>

                            <div className="cps__services">
                                <div className="cps__services-title">
                                    <span>Career preparation studio</span>
                                    <h3>Support designed around the student.</h3>
                                </div>

                                <div className="cps__service-list">
                                    {[
                                        ["CV", "Resume & LinkedIn clinics"],
                                        ["GD", "Group discussion practice"],
                                        ["PI", "Mock interviews"],
                                        ["AT", "Aptitude training"],
                                        ["IN", "Internship assistance"],
                                        ["AL", "Alumni mentoring"],
                                    ].map(([icon, title]) => (
                                        <div className="cps__service" key={icon}>
                                            <span>{icon}</span>
                                            <strong>{title}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="cps__recruiters" aria-label="Recruiter network">
                        <div className="cps__container cps__recruiters-head">
                            <span>Recruiter network</span>
                            <p>Organisations representing diverse industries and career paths.</p>
                        </div>

                        <div className="cps__ticker">
                            <div className="cps__ticker-track">
                                {[...recruiters, ...recruiters].map((recruiter, index) => (
                                    <img key={`${recruiter}-${index}`} src={recruiter} />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="cps__cta">
                        <div className="cps__container cps__cta-grid">
                            <div>
                                <span className="cps__eyebrow cps__eyebrow--gold">
                                    Ready for what comes next?
                                </span>
                                <h2>Build the skills. Meet the industry. Begin your journey.</h2>
                            </div>
                            <div className="cps__cta-actions">
                                <a href="/apply-online">Apply to ISBM University <span>↗</span></a>
                                <a href="mailto:placements@isbmuniversity.edu.in">
                                    Contact career services <span>→</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Admission Process & Eligibility */}
                <section className="aes" aria-labelledby="admission-eligibility-title">
                    <div className="aes__intro">
                        <div className="aes__container aes__intro-grid">
                            <div>
                                <span className="aes__eyebrow">Admissions 2026</span>
                                <h2 id="admission-eligibility-title">
                                    Your journey to the School of Management
                                    <em> starts here.</em>
                                </h2>
                            </div>

                            <div className="aes__intro-side">
                                <p>
                                    A transparent admission pathway helps you understand the process,
                                    prepare the right documents and identify the management programme
                                    that matches your academic and professional goals.
                                </p>
                                <div className="aes__intro-actions">
                                    <a href="/apply-online">Apply online <span>↗</span></a>
                                    <a href="/admissions">Admission help <span>→</span></a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="aes__process">
                        <div className="aes__container">
                            <div className="aes__process-head">
                                <span>Application pathway</span>
                                <small>Five clear steps</small>
                            </div>

                            <div className="aes__step-list">
                                {admissionSteps.map((step, index) => (
                                    <article className="aes__step" key={step.number}>
                                        <div className="aes__step-top">
                                            <span>{step.number}</span>
                                            <i aria-hidden="true">{index < admissionSteps.length - 1 ? "→" : "✓"}</i>
                                        </div>
                                        <h3>{step.title}</h3>
                                        <p>{step.text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="aes__eligibility">
                        <div className="aes__container">
                            <header className="aes__section-head">
                                <div>
                                    <span className="aes__eyebrow">Programme eligibility</span>
                                    <h2>Find the programme that fits your next step.</h2>
                                </div>
                                <p>
                                    Select a programme to review indicative academic eligibility,
                                    programme level and selection information.
                                </p>
                            </header>

                            <div className="aes__eligibility-shell">
                                <div
                                    className="aes__programme-tabs"
                                    role="tablist"
                                    aria-label="Management programmes"
                                >
                                    {programmes2.map((programme, index) => (
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={activeProgramme.id === programme.id}
                                            aria-controls="management-eligibility-panel"
                                            className={activeProgramme.id === programme.id ? "is-active" : ""}
                                            onClick={() => setActiveProgramme(programme)}
                                            key={programme.id}
                                        >
                                            <small>{String(index + 1).padStart(2, "0")}</small>
                                            <span>{programme.shortName}</span>
                                            <i>→</i>
                                        </button>
                                    ))}
                                </div>

                                <article
                                    className="aes__programme-panel"
                                    id="management-eligibility-panel"
                                    role="tabpanel"
                                    aria-live="polite"
                                    key={activeProgramme.id}
                                >
                                    <div className="aes__programme-title">
                                        <span>{activeProgramme.level} programme</span>
                                        <h3>{activeProgramme.name}</h3>
                                    </div>

                                    <div className="aes__programme-facts">
                                        <div>
                                            <small>Duration</small>
                                            <strong>{activeProgramme.duration}</strong>
                                        </div>
                                        <div>
                                            <small>Entry qualification</small>
                                            <strong>{activeProgramme.qualification}</strong>
                                        </div>
                                    </div>

                                    <div className="aes__programme-details">
                                        <div>
                                            <span>Eligibility guidance</span>
                                            <p>{activeProgramme.eligibility}</p>
                                        </div>
                                        <div>
                                            <span>Selection process</span>
                                            <p>{activeProgramme.selection}</p>
                                        </div>
                                        <div>
                                            <span>Ideal for</span>
                                            <p>{activeProgramme.idealFor}</p>
                                        </div>
                                    </div>

                                    <div className="aes__programme-bottom">
                                        <p>
                                            Final eligibility is subject to verification and the
                                            University’s current admission regulations.
                                        </p>
                                        <a href={`/programmes/${activeProgramme.id}`}>
                                            View programme details <span>↗</span>
                                        </a>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </div>

                    <div className="aes__support">
                        <div className="aes__container aes__support-grid">
                            <div className="aes__documents">
                                <div className="aes__support-title">
                                    <span>Application checklist</span>
                                    <h2>Keep these documents ready.</h2>
                                </div>

                                <div className="aes__document-list">
                                    {documents.map((document, index) => (
                                        <div key={document}>
                                            <span>0{index + 1}</span>
                                            <strong>{document}</strong>
                                            <i aria-hidden="true">✓</i>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <aside className="aes__help">
                                <span className="aes__help-icon" aria-hidden="true">?</span>
                                <small>Need admission assistance?</small>
                                <h3>Speak with our admissions team.</h3>
                                <p>
                                    Get support with programme selection, eligibility, documents and
                                    the online application process.
                                </p>
                                <a href="tel:+919373199999">
                                    Call +91-9373199999 <span>↗</span>
                                </a>
                                <a href="/request-callback">
                                    Request a callback <span>→</span>
                                </a>
                            </aside>
                        </div>
                    </div>
                </section>

                <Alumni />

                {/* FAQ */}
                <section className="mfaq" aria-labelledby="management-faq-title">
                    <div className="mfaq__container">
                        <header className="mfaq__header">
                            <div>
                                <span className="mfaq__eyebrow">Frequently asked questions</span>
                                <h2 id="management-faq-title">
                                    Clear answers for your
                                    <em> management journey.</em>
                                </h2>
                            </div>
                            <p>
                                Explore common questions about programmes, eligibility, learning,
                                career preparation and the admission process at the School of
                                Management.
                            </p>
                        </header>

                        <div className="mfaq__layout">
                            <aside className="mfaq__guide">
                                <span className="mfaq__guide-index" aria-hidden="true">?</span>
                                <small>Find your answer</small>
                                <h3>What would you like to know?</h3>

                                <label className="mfaq__search">
                                    <span className="sr-only">Search frequently asked questions</span>
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <circle cx="11" cy="11" r="7" />
                                        <path d="m20 20-4-4" />
                                    </svg>
                                    <input
                                        type="search"
                                        value={search}
                                        onChange={(event) => {
                                            setSearch(event.target.value);
                                            setOpenQuestion(0);
                                        }}
                                        placeholder="Search questions..."
                                    />
                                </label>

                                <div className="mfaq__categories" aria-label="FAQ categories">
                                    {categories.map((category) => (
                                        <button
                                            type="button"
                                            className={activeCategory === category ? "is-active" : ""}
                                            aria-pressed={activeCategory === category}
                                            onClick={() => chooseCategory(category)}
                                            key={category}
                                        >
                                            <span>{category}</span>
                                            <small>
                                                {category === "All"
                                                    ? faqs.length
                                                    : faqs.filter((item) => item.category === category).length}
                                            </small>
                                        </button>
                                    ))}
                                </div>

                                <div className="mfaq__contact">
                                    <span>Still need assistance?</span>
                                    <a href="/contact-us">Ask our admissions team <i>↗</i></a>
                                </div>
                            </aside>

                            <div className="mfaq__questions">
                                <div className="mfaq__questions-top">
                                    <span>
                                        {activeCategory === "All" ? "All questions" : activeCategory}
                                    </span>
                                    <small>
                                        {visibleFaqs.length} {visibleFaqs.length === 1 ? "answer" : "answers"}
                                    </small>
                                </div>

                                {visibleFaqs.length ? (
                                    <div className="mfaq__accordion">
                                        {visibleFaqs.map((item, index) => {
                                            const isOpen = openQuestion === index;
                                            const contentId = `management-faq-answer-${index}`;

                                            return (
                                                <article
                                                    className={`mfaq__item ${isOpen ? "is-open" : ""}`}
                                                    key={`${item.category}-${item.question}`}
                                                >
                                                    <button
                                                        type="button"
                                                        aria-expanded={isOpen}
                                                        aria-controls={contentId}
                                                        onClick={() => setOpenQuestion(isOpen ? -1 : index)}
                                                    >
                                                        <span className="mfaq__number">
                                                            {String(index + 1).padStart(2, "0")}
                                                        </span>
                                                        <span className="mfaq__question-copy">
                                                            <small>{item.category}</small>
                                                            <strong>{item.question}</strong>
                                                        </span>
                                                        <span className="mfaq__toggle" aria-hidden="true">
                                                            <i />
                                                            <i />
                                                        </span>
                                                    </button>

                                                    <div
                                                        className="mfaq__answer"
                                                        id={contentId}
                                                        hidden={!isOpen}
                                                    >
                                                        <p>{item.answer}</p>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="mfaq__empty">
                                        <strong>No matching questions found.</strong>
                                        <p>Try a shorter keyword or select another category.</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearch("");
                                                chooseCategory("All");
                                            }}
                                        >
                                            Clear search
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Lead Footer */}
                <ManagementLeadFooterSection />

                {/* Footer */}
                <Footer />
            </main>
        </>
    );
}

export default Management;

